import React, {
    ChangeEventHandler,
    FC,
    useCallback,
    useEffect,
    useState,
} from "react";
import { get } from "lodash";

import { defined } from "@formant/core/common/defined";
import { IGroup } from "@formant/core/model/IGroup";
import { StreamType } from "@formant/core/model/StreamType";
import { TextField } from "@formant/ui/component/common/TextField";

import { groupClient, streamClient } from "../../clients";
import { useStores } from "../../context/StoreContext";
import { SelectField } from "../common/SelectField";
import { evaluateExpression } from "./evaluateExpression";
import { Label } from "./Label";
import { IInputProps, IJsonStringSchema } from "./types";
import { updatePath } from "./updatePath";

export const TextInput: FC<IInputProps<IJsonStringSchema>> = props => {
    const { params, schema, setParams, path, validation, currentPath } = props;
    const [streams, setStreams] = useState<string[] | undefined>(undefined);
    const [groups, setGroups] = useState<IGroup[] | undefined>(undefined);
    const [isVisible, setIsVisible] = useState(true);

    const invalid = validation && validation?.exactPath === path.join("/");

    const { authStore } = useStores();

    useEffect(() => {
        if (
            !evaluateExpression(params, path, schema["$formant.visible.when"])
        ) {
            setIsVisible(false);
        } else {
            if (!isVisible) {
                setParams(prev =>
                    updatePath(prev, path, schema.default || undefined),
                );
            }
            setIsVisible(true);
        }
    }, [params, isVisible]);

    useEffect(() => {
        const currentValue = get(params, path);
        if (
            currentValue === undefined &&
            schema.default !== undefined &&
            evaluateExpression(params, path, schema["$formant.visible.when"])
        ) {
            setParams(prev => updatePath(prev, path, schema.default));
        }
    }, []);

    const handleSelectChange = useCallback<
        ChangeEventHandler<HTMLSelectElement>
    >(
        e => setParams(prev => updatePath(prev, path, e.target.value)),
        [path, setParams],
    );

    const handleChange = useCallback<ChangeEventHandler<HTMLInputElement>>(
        e =>
            setParams(prev => {
                const value = e.target.value;
                const last = [...path].pop();
                // if field is empty, unset it.
                if ((value === null || value === "") && last) {
                    const previousValue = get(prev, currentPath);
                    const updates = { ...previousValue };
                    delete updates[last];
                    return updatePath(prev, currentPath, updates);
                }
                return updatePath(prev, path, value);
            }),
        [path, setParams],
    );

    const fetchStreamsByTypes = async (streamTypes: StreamType[]) => {
        const token = await authStore.token();
        const allStreams = await streamClient.list(token);
        const streamNames = allStreams
            .filter(
                _ =>
                    streamTypes.length === 0 ||
                    streamTypes.includes(_.streamType),
            )
            .map(_ => _.streamName);
        setStreams(streamNames);
    };

    const fetchGroups = async () => {
        const token = await authStore.token();
        const allGroups = await groupClient.list(token);
        setGroups(allGroups);
    };

    useEffect(() => {
        const streamType = schema["$formant.streams.byType"];
        if (streamType !== undefined) {
            let streamTypes;
            if (typeof streamType === "string") {
                streamTypes = streamType
                    .split(",")
                    .map(_ => _.trim())
                    .filter(_ => _ !== "");
            } else {
                streamTypes = streamType;
            }
            fetchStreamsByTypes(streamTypes as StreamType[]).catch(() => {
                throw new Error("Failed to fetch streams");
            });
        }
        if (schema["$formant.groups"]) {
            fetchGroups().catch(() => {
                throw new Error("Failed to fetch groups");
            });
        }
    }, []);

    if (!isVisible) {
        return null;
    }

    if (schema.enum) {
        const values = schema.enum || [];
        return (
            <>
                <Label
                    value={schema.title}
                    description={schema.description}
                    link={schema["$formant.documentationUrl"]}
                    invalid={invalid}
                />
                <SelectField
                    small={true}
                    onChange={handleSelectChange}
                    value={get(params, path) ?? ""}
                    invalid={invalid}
                    errorMessage={invalid ? "required" : undefined}
                >
                    <option value="">Select</option>
                    {values.map((value, index) => (
                        <option key={index} value={value}>
                            {value}
                        </option>
                    ))}
                </SelectField>
            </>
        );
    }

    if (schema["$formant.groups"]) {
        const values = groups || [];
        return (
            <>
                <Label
                    value={schema.title}
                    description={schema.description}
                    link={schema["$formant.documentationUrl"]}
                    invalid={invalid}
                />
                <SelectField
                    small={true}
                    onChange={handleSelectChange}
                    value={get(params, path) ?? ""}
                    invalid={invalid}
                    errorMessage={invalid ? "required" : undefined}
                >
                    <option value="">Select</option>
                    {values.map((value, index) => (
                        <option key={index} value={defined(value.id)}>
                            {value.name}
                        </option>
                    ))}
                </SelectField>
            </>
        );
    }

    if (schema["$formant.date"]) {
        return (
            <>
                <Label
                    value={schema.title}
                    description={schema.description}
                    link={schema["$formant.documentationUrl"]}
                    invalid={invalid}
                />
                <input
                    type="datetime-local"
                    value={get(params, path) ?? ""}
                    onChange={handleChange}
                />
            </>
        );
    }

    return (
        <>
            <Label
                value={schema.title}
                description={schema.description}
                link={schema["$formant.documentationUrl"]}
                invalid={invalid}
            />
            <TextField
                small={true}
                onChange={handleChange}
                value={get(params, path) ?? ""}
                placeholder={schema["$formant.placeholder"] || "Edit"}
                list={streams}
                invalid={invalid}
                errorMessage={invalid ? "required" : undefined}
            />
        </>
    );
};
